import { Link } from '@tanstack/react-router'
import { Collapse, type CollapseProps } from 'antd'
import { formatDistanceToNow } from 'date-fns'
import {
  buildEventsByComponentId,
  sortMachinedForgingAssetModelComponents,
} from 'features/assetDatabase/components/assetOverview/assetHistory/eventsListUtils'
import React, { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  GeckoAssetModel,
  GeckoInspection,
  GeckoMachinedForgingSimple,
  GeckoMillPlan,
  machinedForgingStageColorMap,
  MachinedForgingStageContainer,
  RolodexAuditLogObject,
} from 'types'
import { parseDateAndSetToEST } from 'utils/parsingHelpers'

const PageContainer = styled.div`
  margin-top: 0.6rem;
  width: 100%;
  overflow: auto;
`

const EventContainer = styled.div`
  text-align: left;
`

const TimestampText = styled.div`
  font-size: 0.85em;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-bottom: 0.1rem;
`

const EventText = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 0.1rem;
`

const SmallLinkText = styled(Link)`
  font-size: 0.9em;
  color: ${({ theme }) => theme.forecast.defaultText};
  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
  text-decoration: underline;
  display: block;
`

const EventListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const StageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

interface EventsListProps {
  assetModel: GeckoAssetModel
  inspections: readonly GeckoInspection[]
  millPlans: readonly GeckoMillPlan[]
  assetAuditLogs: RolodexAuditLogObject | undefined
  sortAscending: boolean
}

const formatDate = (date: Date | undefined): string => {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }
  return 'N/A'
}

export const EventsList: React.FC<EventsListProps> = ({
  assetModel,
  inspections,
  millPlans,
  assetAuditLogs,
  sortAscending = false,
}) => {
  const theme = useTheme()
  const { sortedComponents, eventsByComponentId } = useMemo(() => {
    const sortedComponents = sortMachinedForgingAssetModelComponents(
      assetModel,
      sortAscending
    )
    const eventsByComponentId = buildEventsByComponentId(
      sortedComponents,
      inspections,
      millPlans,
      assetAuditLogs,
      sortAscending
    )
    return {
      sortedComponents,
      eventsByComponentId,
    }
  }, [assetModel, inspections, millPlans, assetAuditLogs, sortAscending])

  if (!assetModel) {
    return <PageContainer>No asset models to display.</PageContainer>
  }

  const collapseItems: CollapseProps['items'] = sortedComponents
    .map((component) => {
      if (!(component instanceof GeckoMachinedForgingSimple)) return null

      const stage = component.stage
      const stageColor = stage
        ? machinedForgingStageColorMap.get(stage)
        : undefined
      const stageTag = stage ? (
        <MachinedForgingStageContainer
          bgColor={stageColor}
          textColor={theme.forecast.background2}>
          {stage}
        </MachinedForgingStageContainer>
      ) : (
        'Unknown Stage'
      )
      const manufacturedDate = component.manufactured_date
        ? parseDateAndSetToEST(component.manufactured_date)
        : undefined

      const componentEvents = eventsByComponentId[component.id] || []

      return {
        key: component.id,
        label: (
          <StageContainer title={manufacturedDate?.toLocaleString()}>
            {`Advanced to stage `}
            {stageTag}
            {`on ${formatDate(manufacturedDate)}`}
          </StageContainer>
        ),
        children: (
          <EventListWrapper>
            {componentEvents.map((event) => (
              <EventContainer key={event.id}>
                <TimestampText title={event.date.toLocaleString()}>
                  {formatDistanceToNow(event.date, { addSuffix: true })}
                </TimestampText>
                <EventText>{event.event_text}</EventText>
                {event.url && event.type === 'inspection' && (
                  <SmallLinkText to={event.url} from='/assets/$siteID/$unitID'>
                    View Inspection
                  </SmallLinkText>
                )}
                {event.url && event.type === 'mill-plan' && (
                  <SmallLinkText to={event.url} from='/assets/$siteID/$unitID'>
                    View Mill Plan
                  </SmallLinkText>
                )}
              </EventContainer>
            ))}
            {componentEvents.length === 0 && <p>No events for this stage.</p>}
          </EventListWrapper>
        ),
      }
    })
    .filter(Boolean) as CollapseProps['items']

  return (
    <PageContainer>
      <Collapse
        items={collapseItems}
        defaultActiveKey={
          sortedComponents[0]?.id ? [sortedComponents[0].id] : undefined
        }
      />
    </PageContainer>
  )
}
